import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { useLoggerWithContext } from '../services/logger/loggerContext';

/**
 * Detects when a previously-authenticated Auth0 session expires and
 * redirects to login before mounted modules see `isAuthenticated: false`.
 *
 * Normal token refresh (~30 min access token expiry) is handled seamlessly
 * by the Auth0 SDK via `getAccessTokenSilently()` — the user never notices.
 * This hook only fires when silent refresh itself fails, meaning the Auth0
 * session is truly dead (session cookie expired, third-party cookies blocked,
 * Auth0 unreachable, etc.).
 *
 * By redirecting at the shell level, mounted modules never encounter an
 * unauthenticated state and don't need to handle session expiry themselves.
 */
export function useSessionExpiryGuard(): void {
  const { isAuthenticated, isLoading, login } = useAuth();
  const logger = useLoggerWithContext();
  const wasAuthenticatedRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticatedRef.current = true;
      return;
    }

    // Session expired: user was authenticated, Auth0 is done loading, and
    // isAuthenticated is now false. Redirect to Auth0 login.
    if (wasAuthenticatedRef.current && !isAuthenticated && !isLoading) {
      logger.warn('Auth0 session expired — redirecting to login', {
        auth_event: 'session_expiry_redirect',
      });
      login();
    }
  }, [isAuthenticated, isLoading, login, logger]);
}
